import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { LeagueSettings, DEFAULT_LEAGUE_SETTINGS, DEFAULT_CATEGORY_CONFIG, LeagueType, ScoringFormat, CategoryStyle } from '@/types/leagueSetup';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSport } from '@/contexts/SportContext';
import { trackEvent } from '@/lib/analytics';
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Trophy,
  Users,
  Settings,
  Bomb,
  DollarSign,
  Gavel,
  AlertTriangle,
  BarChart3,
  Calendar,
  Copy,
  Share2,
  PartyPopper,
} from 'lucide-react';
import { 
  faabBudgetSchema, 
  regularSeasonWeeksSchema, 
  sabotageSpinCostSchema,
  parseIntegerInput 
} from '@/lib/validationSchemas';

interface CreateLeagueWizardProps {
  onComplete: (leagueId?: string) => void;
  onCancel: () => void;
}

const steps = [
  { id: 'basics', title: 'League Basics', icon: Trophy },
  { id: 'league-type', title: 'League Type', icon: BarChart3 },
  { id: 'scoring', title: 'Scoring', icon: Settings },
  { id: 'draft', title: 'Draft Settings', icon: Gavel },
  { id: 'roster', title: 'Roster & Waivers', icon: Users },
  { id: 'sabotage', title: 'Sabotage Rules', icon: Bomb },
  { id: 'review', title: 'Review & Create', icon: Check },
];

// Sabotage League is always "managed" format - Worst Ball is a separate tournament type

const CreateLeagueWizard = ({ onComplete, onCancel }: CreateLeagueWizardProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [settings, setSettings] = useState<LeagueSettings>(DEFAULT_LEAGUE_SETTINGS);
  const [sabotageEnabled, setSabotageEnabled] = useState(true);
  const [sabotageSpinCost, setSabotageSpinCost] = useState(0);
  const [draftSabotageEnabled, setDraftSabotageEnabled] = useState(true);
  const [draftSabotageSpinCost, setDraftSabotageSpinCost] = useState(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);
  const [createdLeague, setCreatedLeague] = useState<{ id: string; invite_code: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { activeSport } = useSport();

  const validateField = (field: string, value: number, schema: any): boolean => {
    const result = schema.safeParse(value);
    if (!result.success) {
      setValidationErrors(prev => ({ ...prev, [field]: result.error.errors[0]?.message || 'Invalid value' }));
      return false;
    }
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    return true;
  };

  const handleFaabChange = (value: string) => {
    const parsed = parseIntegerInput(value, 100);
    validateField('faabBudget', parsed, faabBudgetSchema);
    updateSettings({ faabBudget: parsed });
  };

  const handleWeeksChange = (value: string) => {
    const parsed = parseIntegerInput(value, 13);
    validateField('regularSeasonWeeks', parsed, regularSeasonWeeksSchema);
    updateSettings({ regularSeasonWeeks: parsed });
  };

  const handleSpinCostChange = (value: string) => {
    const parsed = parseIntegerInput(value, 10);
    validateField('sabotageSpinCost', parsed, sabotageSpinCostSchema);
    setSabotageSpinCost(parsed);
  };

  const updateSettings = (updates: Partial<LeagueSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleCreate = async () => {
    if (!user) return;
    setCreating(true);
    try {
      // 1. Create the league
      const { data: league, error: leagueError } = await supabase
        .from('leagues')
        .insert({
          name: settings.name,
          sport: activeSport,
          commissioner_id: user.id,
          team_count: settings.teamCount,
          league_type: settings.leagueType,
          scoring_format: settings.scoringFormat,
          scoring_type: settings.scoringType,
          draft_type: settings.draftType,
          waiver_type: settings.waiverType,
          faab_budget: settings.faabBudget,
          playoff_teams: settings.playoffTeams,
          regular_season_weeks: settings.regularSeasonWeeks,
          category_config: settings.categoryConfig ? settings.categoryConfig as any : null,
          sabotage_enabled: sabotageEnabled,
          sabotage_spin_cost: sabotageSpinCost,
          draft_sabotage_enabled: draftSabotageEnabled,
          draft_sabotage_spin_cost: draftSabotageSpinCost,
        })
        .select()
        .single();

      if (leagueError) throw leagueError;

      // 2. Create the commissioner's team
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .insert({
          league_id: league.id,
          user_id: user.id,
          name: `${user.user_metadata?.full_name || 'My'}'s Team`,
          faab_remaining: settings.waiverType.startsWith('faab') ? settings.faabBudget : null,
        })
        .select()
        .single();

      if (teamError) throw teamError;

      // 3. Add commissioner as league member
      const { error: memberError } = await supabase
        .from('league_members')
        .insert({
          league_id: league.id,
          user_id: user.id,
          team_id: team.id,
          role: 'commissioner',
        });

      if (memberError) throw memberError;

      setCreatedLeague({ id: league.id, invite_code: league.invite_code });
      trackEvent('league_created', { league_id: league.id, league_name: settings.name, team_count: settings.teamCount, scoring_type: settings.scoringType });
      toast({
        title: "League Created! 🎉",
        description: `${settings.name} is ready.`,
      });
    } catch (error: any) {
      console.error('League creation error:', error);
      toast({
        title: "Failed to create league",
        description: error.message || 'Something went wrong.',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const renderStepContent = () => {
    switch (steps[currentStep].id) {
      case 'basics':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">League Name</Label>
              <Input
                id="name"
                placeholder="Enter league name..."
                value={settings.name}
                onChange={(e) => updateSettings({ name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Number of Teams</Label>
              <Select
                value={settings.teamCount.toString()}
                onValueChange={(v) => updateSettings({ teamCount: parseInt(v) as any })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[8, 10, 12, 14, 16].map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n} Teams
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Playoff Teams</Label>
              <Select
                value={settings.playoffTeams.toString()}
                onValueChange={(v) => updateSettings({ playoffTeams: parseInt(v) as any })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 Teams</SelectItem>
                  <SelectItem value="6">6 Teams</SelectItem>
                  <SelectItem value="8">8 Teams</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'league-type':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>League Type</Label>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { id: 'h2h-points', name: 'H2H Points', desc: 'Head-to-head matchups with cumulative weekly/daily point totals (e.g., 1152–1096)', icon: '🏆' },
                  { id: 'h2h-cats', name: 'H2H Categories', desc: 'Compete across multiple stat categories each matchup, win each category individually', icon: '📊' },
                  { id: 'roto', name: 'Rotisserie', desc: 'Season-long cumulative rankings across all stat categories, no head-to-head matchups', icon: '🔄' },
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      const updates: Partial<LeagueSettings> = { leagueType: type.id as LeagueType };
                      if (type.id === 'h2h-cats' || type.id === 'roto') {
                        updates.categoryConfig = settings.categoryConfig || DEFAULT_CATEGORY_CONFIG;
                      }
                      updateSettings(updates);
                    }}
                    className={cn(
                      "p-4 rounded-lg border text-left transition-all",
                      settings.leagueType === type.id
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{type.icon}</span>
                        <div>
                          <p className="font-medium">{type.name}</p>
                          <p className="text-sm text-muted-foreground">{type.desc}</p>
                        </div>
                      </div>
                      {settings.leagueType === type.id && (
                        <Check className="w-5 h-5 text-primary shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Scoring Format</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'weekly', name: 'Weekly', desc: 'Full-week matchups using all games', icon: Calendar },
                  { id: 'daily', name: 'Daily', desc: 'Single-day slates, scores reset daily', icon: Calendar },
                ].map((format) => (
                  <button
                    key={format.id}
                    onClick={() => updateSettings({ scoringFormat: format.id as ScoringFormat })}
                    className={cn(
                      "p-3 rounded-lg border text-left transition-all",
                      settings.scoringFormat === format.id
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <p className="font-medium text-sm">{format.name}</p>
                    <p className="text-xs text-muted-foreground">{format.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Category config for cat leagues */}
            {(settings.leagueType === 'h2h-cats' || settings.leagueType === 'roto') && settings.categoryConfig && (
              <>
                <Separator />
                <div className="space-y-4">
                  <Label>Category Style</Label>
                  <div className="grid grid-cols-1 gap-3">
                    {[
                      { id: 'grouped', name: 'Grouped Categories', desc: 'Use scoring rule groups (Passing, Rushing, etc.) as categories' },
                      { id: 'individual', name: 'Individual Stats', desc: 'Specific stats act as categories (e.g., Total INTs, Total Fumbles)' },
                    ].map((style) => (
                      <button
                        key={style.id}
                        onClick={() => updateSettings({
                          categoryConfig: { ...settings.categoryConfig!, style: style.id as CategoryStyle }
                        })}
                        className={cn(
                          "p-3 rounded-lg border text-left transition-all",
                          settings.categoryConfig?.style === style.id
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        <p className="font-medium text-sm">{style.name}</p>
                        <p className="text-xs text-muted-foreground">{style.desc}</p>
                      </button>
                    ))}
                  </div>

                  {settings.leagueType === 'h2h-cats' && (
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Each Category = 1 Win</Label>
                        <p className="text-xs text-muted-foreground">
                          Each category won counts as a separate win in standings
                        </p>
                      </div>
                      <Switch
                        checked={settings.categoryConfig?.winsPerCategory ?? true}
                        onCheckedChange={(checked) => updateSettings({
                          categoryConfig: { ...settings.categoryConfig!, winsPerCategory: checked }
                        })}
                      />
                    </div>
                  )}

                  {/* SAB-903 / SAB-782: this setting now actually drives standings order
                      (MLBBetaFantasySchedule.buildStandings). It was collected and ignored
                      since launch. "No Tiebreaker" is removed -- seeding must always be
                      determined, and an option meaning "leave ties unresolved" cannot be
                      honoured by anything that has to produce a bracket. */}
                  <div className="space-y-2">
                    <Label>Playoff Seeding Tiebreaker</Label>
                    <Select
                      value={settings.categoryConfig?.tiebreaker === 'head-to-head' ? 'head-to-head' : 'total-points'}
                      onValueChange={(v) => updateSettings({
                        categoryConfig: { ...settings.categoryConfig!, tiebreaker: v as any }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="total-points">Total Points</SelectItem>
                        <SelectItem value="head-to-head">Head-to-Head Record</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">
                      Teams are ranked by wins; this decides ties. Head-to-head counts wins minus
                      losses between the tied teams, falling back to total points if they never
                      met or are level.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 'scoring':
        const scoringOptions = activeSport === 'mlb'
          ? [
              { id: 'trash', name: 'Trash Points', desc: 'Score points for strikeouts, errors, blown saves, and mistakes' },
              { id: 'standard', name: 'Standard', desc: 'Traditional fantasy baseball scoring' },
            ]
          : [
              { id: 'trash', name: 'Trash Points', desc: 'Score points for turnovers, sacks, and mistakes' },
              { id: 'standard', name: 'Standard', desc: 'Traditional fantasy scoring' },
              { id: 'ppr', name: 'PPR', desc: 'Point per reception scoring' },
            ];
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Scoring Type</Label>
              <div className="grid grid-cols-1 gap-3">
                {scoringOptions.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => updateSettings({ scoringType: type.id as any })}
                    className={cn(
                      "p-4 rounded-lg border text-left transition-all",
                      settings.scoringType === type.id
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{type.name}</p>
                        <p className="text-sm text-muted-foreground">{type.desc}</p>
                      </div>
                      {settings.scoringType === type.id && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'draft':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Draft Type</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'snake', name: 'Snake', desc: 'Traditional reverse order' },
                  { id: 'linear', name: 'Linear', desc: 'Same order each round' },
                  { id: 'auction', name: 'Auction', desc: 'Bid on players' },
                  { id: 'slow', name: 'Slow', desc: 'Async over days' },
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => updateSettings({ draftType: type.id as any })}
                    className={cn(
                      "p-3 rounded-lg border text-left transition-all",
                      settings.draftType === type.id
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <p className="font-medium text-sm">{type.name}</p>
                    <p className="text-xs text-muted-foreground">{type.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'roster':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Waiver Type</Label>
              <Select
                value={settings.waiverType}
                onValueChange={(v) => updateSettings({ waiverType: v as any })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free_agent">Free Agent (First Come First Serve)</SelectItem>
                  <SelectItem value="faab_nightly">FAAB: Nightly (Bids process daily)</SelectItem>
                  <SelectItem value="faab_sunday">FAAB: Weekly (Bids process Sundays)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(settings.waiverType === 'faab_nightly' || settings.waiverType === 'faab_sunday') && (
              <div className="space-y-2">
                <Label>FAAB Budget</Label>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={settings.faabBudget}
                    onChange={(e) => handleFaabChange(e.target.value)}
                    min={0}
                    max={10000}
                  />
                </div>
                {validationErrors.faabBudget && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {validationErrors.faabBudget}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Regular Season Weeks</Label>
              <Input
                type="number"
                value={settings.regularSeasonWeeks}
                onChange={(e) => handleWeeksChange(e.target.value)}
                min={1}
                max={activeSport === 'mlb' ? 26 : 18}
              />
              <p className="text-xs text-muted-foreground">
                {activeSport === 'mlb' ? 'MLB season supports up to 26 weeks' : 'NFL season supports up to 18 weeks'}
              </p>
              {validationErrors.regularSeasonWeeks && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {validationErrors.regularSeasonWeeks}
                </p>
              )}
            </div>
          </div>
        );

      case 'sabotage':
        return (
          <div className="space-y-6">
            {/* Matchup Sabotages */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Matchup Sabotages</Label>
                <p className="text-sm text-muted-foreground">
                  Allow teams to sabotage opponents during matchups
                </p>
              </div>
              <Switch
                checked={sabotageEnabled}
                onCheckedChange={setSabotageEnabled}
              />
            </div>

            {sabotageEnabled && (
              <>
                <div className="space-y-2 pl-1">
                  <Label>Matchup Spin Cost</Label>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      value={sabotageSpinCost}
                      onChange={(e) => handleSpinCostChange(e.target.value)}
                      min={1}
                      max={100}
                    />
                  </div>
                  {validationErrors.sabotageSpinCost && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {validationErrors.sabotageSpinCost}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Cost per spin goes to the league prize pool
                  </p>
                </div>
              </>
            )}

            <Separator />

            {/* Draft Sabotages */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Draft Sabotages</Label>
                <p className="text-sm text-muted-foreground">
                  Allow teams to sabotage opponents during the draft
                </p>
              </div>
              <Switch
                checked={draftSabotageEnabled}
                onCheckedChange={setDraftSabotageEnabled}
              />
            </div>

            {draftSabotageEnabled && (
              <div className="space-y-2 pl-1">
                <Label>Draft Spin Cost</Label>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={draftSabotageSpinCost}
                    onChange={(e) => {
                      const parsed = parseIntegerInput(e.target.value, 15);
                      validateField('draftSabotageSpinCost', parsed, sabotageSpinCostSchema);
                      setDraftSabotageSpinCost(parsed);
                    }}
                    min={1}
                    max={100}
                  />
                </div>
                {validationErrors.draftSabotageSpinCost && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {validationErrors.draftSabotageSpinCost}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Additional sabotage library and limits can be configured in Commissioner Tools after creation
                </p>
              </div>
            )}
          </div>
        );

      case 'review':
        const leagueTypeLabels: Record<LeagueType, string> = {
          'h2h-points': 'H2H Points',
          'h2h-cats': 'H2H Categories',
          'roto': 'Rotisserie',
        };
        return (
          <div className="space-y-4">
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Bomb className="w-5 h-5 text-primary" />
                <span className="font-semibold">Sabotage League</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {settings.scoringFormat === 'daily' ? 'Daily' : 'Weekly'} {leagueTypeLabels[settings.leagueType]} matchups with sabotage mechanics
              </p>
            </div>

            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">League Name</span>
                <span className="font-medium">{settings.name || 'Unnamed League'}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Teams</span>
                <span className="font-medium">{settings.teamCount}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">League Type</span>
                <Badge variant="secondary">{leagueTypeLabels[settings.leagueType]}</Badge>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Format</span>
                <Badge variant="outline" className="capitalize">{settings.scoringFormat}</Badge>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Scoring</span>
                <Badge variant="secondary">
                  {settings.scoringType === 'trash' ? 'Trash Points' : settings.scoringType.toUpperCase()}
                </Badge>
              </div>
              {settings.categoryConfig && (settings.leagueType === 'h2h-cats' || settings.leagueType === 'roto') && (
                <>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Categories</span>
                    <Badge variant="outline" className="capitalize">{settings.categoryConfig.style}</Badge>
                  </div>
                </>
              )}
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Draft</span>
                <span className="font-medium capitalize">{settings.draftType}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Waivers</span>
                <span className="font-medium">
                  {settings.waiverType === 'faab_nightly'
                    ? `FAAB Nightly ($${settings.faabBudget})`
                    : settings.waiverType === 'faab_sunday'
                    ? `FAAB Weekly ($${settings.faabBudget})`
                    : 'Free Agent (FCFS)'}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sabotages</span>
                <Badge variant={sabotageEnabled ? 'default' : 'outline'}>
                  {sabotageEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>

            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm font-medium mb-2">Included Features</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{settings.scoringFormat === 'daily' ? 'Daily' : 'Weekly'} Lineups</Badge>
                <Badge variant="outline">Trades</Badge>
                <Badge variant="outline">Waivers</Badge>
                <Badge variant="outline">Sabotage Wheel</Badge>
                <Badge variant="outline">Prize Pool</Badge>
                {(settings.leagueType === 'h2h-cats' || settings.leagueType === 'roto') && (
                  <Badge variant="outline">Category Tracking</Badge>
                )}
              </div>
            </div>

            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-sm">
                <strong>Ready to create!</strong> After creation, you'll receive an invite link to share with your league members.
              </p>
            </div>
          </div>
        );
    }
  };

  const handleCopyCode = async () => {
    if (!createdLeague) return;
    await navigator.clipboard.writeText(createdLeague.invite_code);
    setCopied(true);
    toast({ title: 'Copied!', description: 'Invite code copied to clipboard.' });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareCode = async () => {
    if (!createdLeague) return;
    const shareData = {
      title: `Join ${settings.name} on Sabotage Fantasy`,
      text: `Join my Sabotage Fantasy league "${settings.name}"! Use invite code: ${createdLeague.invite_code}`,
      url: window.location.origin,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      handleCopyCode();
    }
  };

  // Show success screen after creation
  if (createdLeague) {
    return (
      <div className="space-y-6 pb-20 md:pb-0">
        <div className="flex flex-col items-center text-center space-y-4 pt-6">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <PartyPopper className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-display font-bold text-foreground">League Created!</h1>
          <p className="text-muted-foreground max-w-md">
            <strong>{settings.name}</strong> is ready to go. Share the invite code below with your friends so they can join.
          </p>
        </div>

        <Card className="max-w-md mx-auto border-primary/30">
          <CardContent className="pt-6 space-y-4">
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground font-medium">Your Invite Code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-4xl font-mono font-bold tracking-[0.3em] text-primary">
                  {createdLeague.invite_code}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleCopyCode}
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy Code'}
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleShareCode}
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>

            <Separator />

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Teams</span>
                <span className="font-medium">{settings.teamCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Scoring</span>
                <span className="font-medium">{settings.scoringType === 'trash' ? 'Trash Points' : settings.scoringType.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Draft</span>
                <span className="font-medium capitalize">{settings.draftType}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button size="lg" onClick={() => onComplete(createdLeague.id)}>
            Go to League Dashboard
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-32 overflow-y-auto h-full">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold">Create League</h1>
        <p className="text-muted-foreground">Set up your new fantasy league</p>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <Progress value={progress} className="h-2" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Step {currentStep + 1} of {steps.length}</span>
          <span>{steps[currentStep].title}</span>
        </div>
      </div>

      {/* Step Indicators */}
      <div className="flex justify-between overflow-x-auto pb-2">
        {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <button
              key={step.id}
              onClick={() => index < currentStep && setCurrentStep(index)}
              disabled={index > currentStep}
              className={cn(
                "flex flex-col items-center gap-1 min-w-[60px]",
                index <= currentStep ? "text-primary" : "text-muted-foreground",
                index < currentStep && "cursor-pointer"
              )}
            >
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center",
                index < currentStep && "bg-primary text-primary-foreground",
                index === currentStep && "bg-primary/20 border-2 border-primary",
                index > currentStep && "bg-muted"
              )}>
                {index < currentStep ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span className="text-xs hidden sm:block">{step.title}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <Card>
        <CardHeader>
          <CardTitle>{steps[currentStep].title}</CardTitle>
          <CardDescription>
            {currentStep === 0 && 'Configure basic league information'}
            {currentStep === 1 && 'Choose points, categories, or roto with daily or weekly scoring'}
            {currentStep === 2 && 'Choose how points are calculated'}
            {currentStep === 3 && 'Set up your draft format'}
            {currentStep === 4 && 'Configure roster and waiver settings'}
            {currentStep === 5 && 'Customize sabotage rules'}
            {currentStep === 6 && 'Review your settings and create the league'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderStepContent()}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => currentStep === 0 ? onCancel() : setCurrentStep(currentStep - 1)}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        {currentStep < steps.length - 1 ? (
          <Button 
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={currentStep === 0 && !settings.name}
          >
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleCreate} disabled={creating}>
            <Trophy className="w-4 h-4 mr-2" />
            {creating ? 'Creating...' : 'Create League'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CreateLeagueWizard;
